import { env } from "./lib/env"

export const DEFAULT_AI_LANGUAGE = env.defaultAiLanguage
export const DEFAULT_MODEL = env.defaultAiModel
export const AUTO_SYNC_INTERVAL_DEFAULT = env.autoSyncIntervalDefault
export const DYNAMIC_SYNC_EXPECTED_POSTS_DEFAULT =
  env.dynamicSyncExpectedPostsDefault
export const AUTO_SYNC_INTERVAL_MIN_MINUTES = 5
/** Max auto-sync staleness interval (24 hours). */
export const AUTO_SYNC_INTERVAL_MAX_MINUTES = 24 * 60
export const RETENTION_POST_DAYS_DEFAULT = env.retentionPostDaysDefault
export const RETENTION_LOG_DAYS_DEFAULT = env.retentionLogDaysDefault
export const RETENTION_SHARED_LOG_DAYS_DEFAULT =
  env.retentionSharedLogDaysDefault
export const RETENTION_PAYLOAD_DAYS_DEFAULT = env.retentionPayloadDaysDefault
export const RETENTION_REPORT_DAYS_DEFAULT = env.retentionReportDaysDefault
export const RETENTION_REPORT_MAX_DEFAULT = env.retentionReportMaxDefault
export const THEME_DEFAULT = "dark"

export const WORKSPACE_TABS = [
  { id: "channels", label: "Channels", icon: "Send" },
  { id: "posts", label: "Posts", icon: "List" },
  { id: "action", label: "Action", icon: "Zap" },
  { id: "summary", label: "Summary", icon: "FileText" },
  { id: "tag", label: "Tag", icon: "Tag" },
  { id: "discover", label: "Discover", icon: "Compass" },
  { id: "directory", label: "Directory", icon: "Library" },
  { id: "chat", label: "Chat", icon: "MessageSquare" },
  { id: "history", label: "History", icon: "History" },
  { id: "tools", label: "Tools", icon: "Activity" },
  { id: "requests", label: "Requests", icon: "Gauge" },
  { id: "settings", label: "Settings", icon: "Settings" },
] as const

/**
 * The workspace tabs, as a type.
 *
 * Derived rather than declared, because it used to be neither: `TabType` was a
 * hand-written union in `types.ts` and `VALID_TABS` was copied into both
 * `routes/_tg/workspace.tsx` and `hooks/useWorkspaceTab.ts`. Three lists to
 * keep in step meant adding a tab to two of them left it reachable by URL but
 * silently falling back to `channels`, and the hand-written union had drifted to
 * carry three ids (`db`, `bots`, `logs`) that no tab had rendered for months.
 *
 * `WORKSPACE_TABS` is now the only place a tab is declared.
 */
export type TabType = (typeof WORKSPACE_TABS)[number]["id"]

/**
 * Every tab id, for validating `?tab=`.
 *
 * Deliberately the *unfiltered* list. A closed tab must stay reachable by URL,
 * because opening one is how every deep link, History row and palette command
 * gets a tab in the first place (TABS-01).
 */
export const VALID_TABS: readonly TabType[] = WORKSPACE_TABS.map(
  (tab) => tab.id,
)

/** Stored in Summary.model for externally pasted AI responses (not in-app generation). */
export const PASTED_SUMMARY_MODEL = "external"

export function isPastedSummaryModel(model?: string | null): boolean {
  return model === PASTED_SUMMARY_MODEL
}

/**
 * The model id, as stored on the Summary.
 *
 * It used to map three hardcoded Gemini ids to prettier labels. BYOK-02 deleted
 * that list — an account on OpenRouter reaches several hundred models and the
 * deployment knows the label for none of them — so the id is the label now. A
 * History row from two providers ago still reads correctly, which the lookup
 * could not manage even for Gemini once an id was retired.
 */
export function formatSummaryModelLabel(model?: string | null): string {
  if (!model) return "Unknown"
  if (isPastedSummaryModel(model)) return "External"
  return model
}

export function resolvePastedSummaryModel(optionalName?: string): string {
  const trimmed = optionalName?.trim()
  return trimmed ? trimmed : PASTED_SUMMARY_MODEL
}

export function isPendingSummary(summary: {
  status?: string
  text?: string
}): boolean {
  return summary.status === "pending"
}

/** Its latest Attempt did not finish (SFR-01); never a Copy Prompt item. */
export function isFailedSummary(summary: { status?: string }): boolean {
  return summary.status === "failed"
}

// The languages a setting may name, and the code a Post's Language carries for
// each. The backend's `TRANSLATION_LANGUAGE_CODES` must equal this map, and
// `backend/tests/jobs/test_translation_skips.py` parses this block to hold it so.
export const LANGUAGE_CODES: Record<string, string> = {
  English: "en",
  Persian: "fa",
  Spanish: "es",
  French: "fr",
  German: "de",
  Chinese: "zh",
  Japanese: "ja",
  Russian: "ru",
  Portuguese: "pt",
  Italian: "it",
  Arabic: "ar",
}

export const LANGUAGES = Object.keys(LANGUAGE_CODES)

/** False for a Post with no words or one already in the Translation language.
 *  `und` and unread Posts stay translatable (LANG-04). */
export function needsTranslation(
  language: string | null | undefined,
  translationLanguage: string,
): boolean {
  if (language == null) return true
  return language !== "zxx" && language !== LANGUAGE_CODES[translationLanguage]
}

/** Below this width the app lays out for a phone; one number for every "narrow". */
export const MOBILE_BREAKPOINT = 768

/** The media query for a phone-width screen. */
export const NARROW_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`
